import type { CSSProperties } from 'react';
import { CustomResponse } from './RestaurantPrinterTypes';
import { SpringPage } from './RestaurantRecipeTypes';

export type { CustomResponse };

export type RestaurantTableStatus =
  | 'FREE'
  | 'OCCUPIED'
  | 'RESERVED'
  | 'CLEANING'
  | 'OUT_OF_SERVICE';

export type RestaurantTableShape = 'SQUARE' | 'RECTANGLE' | 'ROUND';

/**
 * Colores con los que se dibuja la mesa (plano visto desde arriba). Se configuran
 * por sucursal en Configuración › Apariencia y se comparten entre la pantalla de
 * Mesas y el tablero de Órdenes.
 */
export interface RestaurantTableTheme {
  tableFillColor: string;
  tableLineColor: string;
  chairFillColor: string;
  chairLineColor: string;
}

export const DEFAULT_RESTAURANT_TABLE_THEME: RestaurantTableTheme = {
  tableFillColor: '#C68A4E',
  tableLineColor: '#1C2434',
  chairFillColor: '#E7E5E4',
  chairLineColor: '#1C2434',
};

/**
 * Traduce un tema a variables CSS (`--rt-*`) para pasarlas por `style`. Sirve para
 * previsualizaciones acotadas; en las pantallas reales se usa el hook
 * `useRestaurantTableTheme`, que las aplica en `:root`.
 */
export const restaurantTableThemeVars = (
  theme?: Partial<RestaurantTableTheme> | null
): CSSProperties => {
  if (!theme) return {};
  const vars: Record<string, string> = {};
  if (theme.tableFillColor) vars['--rt-table-fill'] = theme.tableFillColor;
  if (theme.tableLineColor) vars['--rt-table-line'] = theme.tableLineColor;
  if (theme.chairFillColor) vars['--rt-chair-fill'] = theme.chairFillColor;
  if (theme.chairLineColor) vars['--rt-chair-line'] = theme.chairLineColor;
  return vars as CSSProperties;
};

export interface RestaurantTableDTO {
  id: number;
  branchId: number;
  branchName: string;
  diningRoomId: number | null;
  diningRoomName: string | null;
  code: string;
  name: string;
  capacity: number;
  status: RestaurantTableStatus;
  shape: RestaurantTableShape;
  positionX: number | null;
  positionY: number | null;
  displayOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RestaurantTableForm {
  id?: number;
  diningRoomId: number | '';
  /** Solo lectura: lo asigna el backend. Se conserva para mostrarlo al editar. */
  code?: string;
  name: string;
  capacity: number;
  status: RestaurantTableStatus;
  shape: RestaurantTableShape;
  positionX: number | '';
  positionY: number | '';
  displayOrder: number;
  active?: boolean;
}

export interface RestaurantTableFilters {
  search: string | null;
  diningRoomId: number | null;
  status: RestaurantTableStatus | null;
  active: boolean | null;
  page: number;
  size: number;
}

export type RestaurantTablePage = SpringPage<RestaurantTableDTO>;
